import { HEADERS } from 'constants/requests';
import { errorCounter } from 'utils/error-counter';

import { VOID_FUNCTION } from '../constants';

const WHITELIST_HEADERS = new Set(
  [HEADERS.ACCEPT, HEADERS.CONTENT_TYPE, HEADERS.CORRELATION_ID].map((header) => {
    return header.toLowerCase();
  })
);

class RequestTracer {
  // eslint-disable-next-line class-methods-use-this
  getHeaders(headers) {
    try {
      if (!headers) {
        return null;
      }

      const result = {};
      const entries = Object.entries(headers);

      for (const [key, value] of entries) {
        const header = key.toLowerCase();

        if (!WHITELIST_HEADERS.has(header)) {
          continue;
        }

        result[header] = value;
      }

      return result;
    } catch {
      return 'Error receiving the headers';
    }
  }

  // eslint-disable-next-line class-methods-use-this
  parseFormDataValues(values, callback = VOID_FUNCTION) {
    for (const value of values) {
      let result;

      if (value instanceof Blob) {
        result = 'Blob';
      } else if (value instanceof File) {
        result = 'File';
      } else if (typeof value === 'undefined' || value === null) {
        result = 'null';
      } else {
        result = String(value);
      }

      callback(result);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getRequestBodyFromFormData(data) {
    try {
      let empty = true;
      const result = {};
      const keys = data.keys();

      for (const key of keys) {
        empty = false;
        result[key] ??= [];

        this.parseFormDataValues(data.getAll(key), (value) => {
          result[key].push(value);
        });
      }

      if (empty) {
        return null;
      }

      return { type: 'FormData', data: result };
    } catch {
      return 'Unexpected error during "FormData" body decoder execution';
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getRequestBodyFromObject(data) {
    try {
      if (Object.keys(data).length) {
        return { type: 'Object', data: JSON.parse(data) };
      }

      return null;
    } catch {
      return 'Unexpected error during "Object" body decoder execution';
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getRequestBodyFromURL(url) {
    try {
      let empty = true;
      const result = {};

      url.searchParams.forEach((value, key) => {
        empty = false;

        if (!result[key]) {
          result[key] = value;

          return;
        }

        if (!Array.isArray(result[key])) {
          const previous = result[key];

          result[key] = [previous];
        }

        result[key].push(value);
      });

      if (empty) {
        return null;
      }

      return { type: 'URL', data: result };
    } catch {
      return 'Unexpected error during "URL" body decoder execution';
    }
  }

  getRequestBody(params, url) {
    try {
      const {
        config: { data },
      } = params;

      if (!data) {
        return this.getRequestBodyFromURL(url);
      }

      if (data instanceof FormData) {
        return this.getRequestBodyFromFormData(data);
      }

      return this.getRequestBodyFromObject(data);
    } catch {
      return 'Error receiving the request body';
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getRequestURL(params) {
    try {
      const {
        config: { baseURL, url },
      } = params;

      const isFullUrl = /^http(s)?/g.test(url);

      if (isFullUrl) {
        return new URL(url);
      }

      return new URL(url, baseURL);
    } catch {
      return 'Error receiving the request url';
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getResponseBody(params) {
    try {
      const { data } = params;

      if (typeof data === 'string') {
        return data;
      } else if (data) {
        return data;
      }

      return null;
    } catch {
      return 'Error receiving the response body';
    }
  }

  async info(response) {
    try {
      const { config, headers: responseHeaders } = response;
      const { tracer } = config;

      if (!tracer) {
        return response;
      }

      if (config.method) {
        config.method = config.method.toLowerCase();
      }

      const { headers: requestHeaders, method } = config;

      const correlationId = requestHeaders[HEADERS.CORRELATION_ID];
      const url = this.getRequestURL(response);
      const additional = {
        'correlation-id': correlationId,
        method,
        request: {
          body: this.getRequestBody(response, url),
          headers: this.getHeaders(requestHeaders),
        },
        response: {
          body: this.getResponseBody(response),
          headers: this.getHeaders(responseHeaders),
        },
        url: url.origin + url.pathname,
      };

      await errorCounter.send({ additional, level: 'info' }, undefined, {
        reqid: correlationId,
      });
    } catch {
      //
    }

    return response;
  }
}

export const requestTracer = new RequestTracer();
